import {
  attributeKeys,
  content,
  getBranchNode,
  getFateLine,
  getMainNode,
  type AttributeKey,
  type Choice,
  type Delta,
  type StoryNode
} from './content-adapter'

// game-core is an ESM package without a TypeScript declaration file yet.
// @ts-ignore package exports the browser ESM entry but does not ship .d.ts yet.
import * as core from '@sun-ge-simulator/game-core'

export type Phase = 'choosing' | 'result' | 'dead' | 'fatePrompt' | 'ended'

export interface PendingResult {
  kind: 'main' | 'branch'
  nodeId: string
  nodeIdx?: number
  choiceIdx: number
  title: string
  result: string
  delta: Delta
  next?: { kind: 'main'; nodeIdx: number } | { kind: 'branch'; goto: string | null }
  dualComplete?: boolean
}

type CoreState = Record<string, any>
type CoreContent = Record<string, any>

export interface GameState {
  schemaVersion: 2
  phase: Phase
  nodeIdx: number
  branchId: string | null
  attrs: Record<AttributeKey, number>
  black: number
  risk: number
  rep: number
  clean: number
  flags: Record<string, boolean | number | string>
  nodePicks: (number | undefined)[]
  dualPicks: { sec: number; choiceIdx: number }[]
  branchPicks: { nodeId: string; choiceIdx: number; result: string; delta: Delta }[]
  history: { nodeId: string; title: string; choice: string; result: string; kind: 'main' | 'branch' }[]
  pendingResult: PendingResult | null
  deathInfo: { type: string; text: string } | null
  fateId: string | null
  endingInfo: { kind: 'main' | 'fate'; id: string; title: string; icon: string; text: string } | null
  deaths: string[]
  endingsCollected: string[]
  fateCollected: string[]
  achievements: string[]
  lifeTitles: string[]
  totalDeaths: number
  totalPlays: number
  completedRuns: number
  revives: number
  adRevives: number
  adDate: string
  runStarted: boolean
  saveRevision: number
  /** JSON-safe canonical state persisted with the UI projection. */
  coreState: CoreState
}

export const defaultAttrs = (): Record<AttributeKey, number> => ({
  asset: 50, intel: 50, luck: 50, face: 50, ability: 50, fame: 50, feel: 50
})

const coreContent: CoreContent = { ...content, attrKeys: attributeKeys }
const engine = core.createEngine(coreContent)

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function deterministicRandom(purpose: string): number {
  // Random events are explicit and replayable after a localStorage round trip.
  let hash = 2166136261
  for (let index = 0; index < purpose.length; index += 1) {
    hash ^= purpose.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return ((hash >>> 0) % 1000000) / 1000000
}

function transition(state: CoreState, action: Record<string, unknown>): { state: CoreState; effects: { type?: string }[] } {
  const seed = `${state.totalPlays}:${state.nodeIdx}:${state.branchIdx || ''}:${state.dualPicks.length}:${state.branchPicks.length}`
  return engine.transition(state, action, {
    random: (purpose: string) => deterministicRandom(`${seed}:${purpose}`)
  })
}

function endingForCore(state: CoreState): GameState['endingInfo'] {
  if (!state.endingInfo) return null
  if (state.endingInfo.kind === 'fate') {
    const line = getFateLine(state.fateLineId)
    const variant = state.endingInfo.variant === 'high' ? 'high' : 'cost'
    const selected = line?.[variant]
    return { kind: 'fate', id: `${line?.id || state.fateLineId || 'fate'}:${variant}`, title: selected?.name || '命运线结局', icon: line?.icon || '🗺️', text: selected?.text || '命运线在这里收束。' }
  }
  const ending = content.endings.find(item => item.id === state.endingInfo.endingId)
  return { kind: 'main', id: ending?.id || state.endingInfo.endingId || 'nobody_end', title: ending?.name || '查无此人', icon: ending?.icon || '😴', text: ending?.desc || '活了，也没人记住。' }
}

function historyFor(state: CoreState): GameState['history'] {
  const history: GameState['history'] = []
  const n0 = getMainNode(0)
  for (const pick of Array.isArray(state.dualPicks) ? state.dualPicks : []) {
    const choice = n0?.choices[pick.choiceIdx]
    if (choice && n0) history.push({ nodeId: n0.id, title: n0.title, choice: choice.text, result: choice.result || '', kind: 'main' })
  }
  for (let nodeIdx = 1; nodeIdx < content.nodes.length; nodeIdx += 1) {
    const node = getMainNode(nodeIdx)
    const choiceIdx = state.nodePicks?.[nodeIdx]
    const choice = node && typeof choiceIdx === 'number' ? node.choices[choiceIdx] : null
    if (node && choice) history.push({ nodeId: node.id, title: node.title, choice: choice.text, result: choice.result || '', kind: 'main' })
  }
  for (const pick of Array.isArray(state.branchPicks) ? state.branchPicks : []) {
    const node = getBranchNode(pick.nodeId)
    const choice = node?.choices[pick.choiceIdx]
    if (node && choice) history.push({ nodeId: node.id, title: node.title, choice: choice.text, result: choice.result || pick.result || '', kind: 'branch' })
  }
  return history
}

function toUi(state: CoreState, previousRevision = 0): GameState {
  const uiState: GameState = {
    schemaVersion: 2, phase: state.phase, nodeIdx: state.nodeIdx, branchId: state.branchIdx || null,
    attrs: clone(state.attrs), black: state.black, risk: state.risk, rep: state.rep, clean: state.clean,
    flags: clone(state.flags), nodePicks: clone(state.nodePicks), dualPicks: clone(state.dualPicks), branchPicks: clone(state.branchPicks),
    history: historyFor(state), pendingResult: clone(state.pendingResult),
    deathInfo: state.deathInfo ? { type: state.deathInfo.type, text: state.deathInfo.text } : null,
    fateId: state.fateLineId || null, endingInfo: endingForCore(state), deaths: clone(state.deaths),
    endingsCollected: clone(state.endingsCollected), fateCollected: clone(state.fateCollected), achievements: clone(state.achievements),
    lifeTitles: clone(state.lifeTitles), totalDeaths: state.totalDeaths, totalPlays: state.totalPlays,
    completedRuns: state.completedRuns, revives: state.revives, adRevives: state.adRevives, adDate: state.adDate, runStarted: state.runStarted,
    saveRevision: previousRevision, coreState: engine.serializeState(state)
  }
  return uiState
}

function legacyToCore(state: Partial<GameState>): CoreState {
  const raw = state as Partial<GameState> & Record<string, any>
  const legacyEnding = raw.endingInfo as (Record<string, any> | null | undefined)
  const fateId = raw.fateId || raw.fateLineId || (legacyEnding?.fateLineId as string | undefined) || null
  const endingKind = legacyEnding?.kind
  const endingId = endingKind === 'main'
    ? (legacyEnding?.id || legacyEnding?.endingId || null)
    : null
  const endingVariant = endingKind === 'fate'
    ? (legacyEnding?.id || legacyEnding?.endingId || '').split(':').pop()
    : null
  return engine.migrateState({
    schemaVersion: 2, nodeIdx: raw.nodeIdx ?? 0, attrs: raw.attrs || defaultAttrs(),
    black: raw.black ?? 0, risk: raw.risk ?? 0, rep: raw.rep ?? 0, clean: raw.clean ?? 0,
    flags: raw.flags || {}, nodePicks: raw.nodePicks || [], nodeDeltas: raw.nodeDeltas || [],
    dualPicks: raw.dualPicks || [], branchPicks: raw.branchPicks || [],
    deaths: raw.deaths || [], endingsCollected: raw.endingsCollected || [], fateCollected: raw.fateCollected || [],
    eggs: raw.eggs || [], achievements: raw.achievements || [], lifeTitles: raw.lifeTitles || [],
    totalDeaths: raw.totalDeaths ?? 0, totalPlays: raw.totalPlays ?? 0,
    legacyTotalPlays: raw.legacyTotalPlays ?? raw.totalPlays ?? 0, completedRuns: raw.completedRuns ?? 0,
    revives: raw.revives ?? 0, power: raw.power ?? 5, adPower: raw.adPower ?? 5,
    adRevives: raw.adRevives ?? 3, adDate: raw.adDate ?? '',
    lastHotIdx: raw.lastHotIdx, lastHotDelta: raw.lastHotDelta, lastDelta: raw.lastDelta, lastHot: raw.lastHot,
    pendingEggs: raw.pendingEggs || [], runStarted: raw.runStarted === true,
    done: raw.done === true || raw.mode === 'ended' || raw.phase === 'ended',
    phase: raw.phase || 'choosing', branchIdx: raw.branchId ?? raw.branchIdx,
    fateLineId: fateId, inFate: Boolean(raw.inFate || fateId),
    mode: raw.mode || ((raw.branchId ?? raw.branchIdx) ? `line:${fateId || ''}` : 'main'),
    pendingResult: raw.pendingResult, deathInfo: raw.deathInfo,
    endingInfo: legacyEnding ? {
      kind: endingKind,
      endingId,
      fateLineId: endingKind === 'fate' ? fateId : null,
      variant: endingKind === 'fate' && (endingVariant === 'high' || endingVariant === 'cost') ? endingVariant : null,
      branchId: legacyEnding.branchId,
      nodeId: legacyEnding.nodeId,
      year: legacyEnding.year,
      age: legacyEnding.age
    } : null
  })
}

function toCore(state: GameState): CoreState {
  try { return engine.migrateState(state.coreState || legacyToCore(state)) } catch { return legacyToCore(state) }
}

function apply(state: GameState, action: Record<string, unknown>): GameState {
  const result = transition(toCore(state), action)
  if (result.effects.some(effect => effect.type === 'error')) return state
  return toUi(result.state, state.saveRevision + 1)
}

export function createInitialState(): GameState { return toUi(engine.createInitialState(), 0) }

export function fromCoreState(serialized: CoreState, saveRevision = 0): GameState {
  return toUi(engine.migrateState(serialized), saveRevision)
}

/**
 * Deserialize either a current UI save, a serialized core save, or a legacy
 * UI-shaped save. Invalid/unknown data is rejected so callers can fall back
 * to a fresh run instead of rendering a broken state.
 */
export function deserializeGameState(value: unknown, saveRevision = 0): GameState | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const candidate = value as Partial<GameState> & { coreState?: CoreState }
  const revision = typeof candidate.saveRevision === 'number' && Number.isFinite(candidate.saveRevision)
    ? candidate.saveRevision
    : saveRevision
  try {
    if (candidate.coreState && typeof candidate.coreState === 'object' && !Array.isArray(candidate.coreState)) {
      return fromCoreState(candidate.coreState, revision)
    }
    return toUi(legacyToCore(candidate), revision)
  } catch {
    return null
  }
}

export function startNewRun(previous?: GameState | null): GameState {
  const base = previous ? toCore(previous) : engine.createInitialState()
  const result = transition(base, { type: 'RESTART' })
  return result.effects.some(effect => effect.type === 'error')
    ? (previous || createInitialState())
    : toUi(result.state, (previous?.saveRevision || 0) + 1)
}

export function currentNode(state: GameState): StoryNode | null {
  const coreState = toCore(state)
  return coreState.branchIdx ? getBranchNode(coreState.branchIdx) : getMainNode(coreState.nodeIdx)
}

export function currentChoices(state: GameState): Choice[] {
  const node = currentNode(state)
  const coreState = toCore(state)
  if (!node) return []
  if (node.id === 'N0') return node.choices.filter(choice => choice.sec === coreState.dualPicks.length)
  return node.choices
}

export function choose(state: GameState, choiceIdx: number): GameState {
  const node = currentNode(state)
  if (!node) return state
  const coreState = toCore(state)
  if (node.id === 'N0') return apply(state, { type: 'PICK_DUAL', choiceIdx })
  return apply(state, coreState.branchIdx ? { type: 'PICK_BRANCH', choiceIdx } : { type: 'PICK_MAIN', nodeIdx: coreState.nodeIdx, choiceIdx })
}

export function continueResult(state: GameState): GameState { return apply(state, { type: 'CONTINUE_RESULT' }) }

export function chooseFate(state: GameState, variant: 'high' | 'cost'): GameState {
  const coreState = toCore(state)
  if (coreState.phase !== 'fatePrompt') return state
  const fate = getFateLine(coreState.fateLineId)
  return fate?.nodes?.length ? apply(state, { type: 'START_BRANCH' }) : apply(state, { type: 'CHOOSE_FATE', variant })
}

export function enterFateBranch(state: GameState): GameState {
  const coreState = toCore(state)
  const fate = getFateLine(coreState.fateLineId)
  return coreState.phase === 'fatePrompt' && Boolean(fate?.nodes?.length)
    ? apply(state, { type: 'START_BRANCH' })
    : state
}

function localDateKey(date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function getReviveStatus(state: GameState, today = localDateKey()): { today: string; date: string; remaining: number; resets: boolean } {
  const date = typeof state.adDate === 'string' ? state.adDate : ''
  const storedRemaining = Number.isFinite(state.adRevives) ? Math.max(0, Math.floor(state.adRevives)) : 0
  return { today, date, remaining: date === today ? storedRemaining : 3, resets: date !== today }
}

export function revive(state: GameState, today = localDateKey()): GameState {
  return apply(state, { type: 'REVIVE', today })
}
export function endingFor(state: GameState) { return state.endingInfo }
export function getDeathType(type: string | undefined) { return content.deathTypes[type || 'nobody'] || content.deathTypes.nobody || { icon: '😐', name: '查无此人', title: '废柴一枚' } }

/** Side-effect-free VIP hint; membership stays outside the engine/save. */
export function choiceDeath(state: GameState, choiceIdx: number): GameState['deathInfo'] {
  if (state.phase !== 'choosing') return null
  const node = currentNode(state)
  if (!node || !currentChoices(state).includes(node.choices[choiceIdx])) return null
  if (state.branchId || node.id === 'N0') return choose(state, choiceIdx).deathInfo
  return engine.predictDeath(toCore(state), state.nodeIdx, choiceIdx) || null
}

export function historicalChoice(state: GameState): number | null {
  if (state.branchId) return null
  const node = currentNode(state)
  const answer = node?.histAns
  const index = typeof answer === 'number' ? answer : answer?.[state.dualPicks.length]
  return typeof index === 'number' && node && currentChoices(state).includes(node.choices[index]) ? index : null
}

/** Original SVIP rule: historical main answer; safest weighted branch gain. */
export function recommendedChoice(state: GameState): number | null {
  if (!state.branchId) return historicalChoice(state)
  const node = currentNode(state)
  if (!node) return null
  let best: number | null = null
  let score = -Infinity
  node.choices.forEach((choice, index) => {
    if (choiceDeath(state, index)) return
    const fx = choice.fx || {}
    const value = attributeKeys.reduce((sum, key) => sum + (fx[key] || 0), 0) - 2 * ((fx.risk || 0) + (fx.black || 0))
    if (value > score) { score = value; best = index }
  })
  return best
}
